import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Pencil, ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/StatusPill";
import { api, apiError } from "@/lib/api";
import { TRUST_META, SOURCE_LABELS, FIELD_LABELS, CLAIM_TYPE_LABELS } from "@/lib/constants";

const EDITABLE = ["marke", "modell", "variante", "generation", "baujahr", "erstzulassung", "karosserie", "motor", "hubraum_ccm", "leistung_kw", "leistung_ps", "kraftstoff", "getriebe", "antrieb", "emissionsklasse", "verbrauch_kombiniert", "co2_g_km", "tueren", "sitze", "farbe_aussen", "farbe_innen", "kilometerstand", "vorbesitzer", "zustand", "standort"];
const NUMERIC = ["baujahr", "hubraum_ccm", "leistung_kw", "leistung_ps", "co2_g_km", "tueren", "sitze", "kilometerstand", "vorbesitzer"];

export default function DataTab({ vehicle, datacheck, reload, onEditPrice }) {
  const [edit, setEdit] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const s = vehicle.specs || {};
  const meta = vehicle.field_meta || {};

  const saveField = async () => {
    setBusy(true);
    try {
      const v = NUMERIC.includes(edit) && value !== "" ? Number(value) : value;
      await api.patch(`/vehicles/${vehicle.id}`, { specs: { [edit]: v }, source: "manuell" });
      toast.success(`${FIELD_LABELS[edit] || edit} aktualisiert`);
      setEdit(null);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resolveConflict = async (conflict, val) => {
    try {
      await api.post(`/vehicles/${vehicle.id}/conflicts/${conflict.id}/resolve`, { value: val });
      toast.success(`Konflikt gelöst: ${FIELD_LABELS[conflict.field] || conflict.field} = ${val}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const claimAction = async (claim, action) => {
    try {
      await api.post(`/vehicles/${vehicle.id}/claims/${claim.id}/${action}`);
      toast.success(action === "confirm" ? "Angabe bestätigt" : "Angabe abgelehnt – wird nicht verwendet");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openConflicts = (vehicle.conflicts || []).filter((c) => !c.resolved);
  const claims = vehicle.claims || [];

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      <Card className="lg:col-span-7">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-display">Fahrzeugdaten mit Quellen-Vertrauen</CardTitle>
          <p className="text-xs text-muted-foreground">Jedes Feld zeigt Quelle und Vertrauensstatus. Änderungen werden protokolliert.</p>
        </CardHeader>
        <CardContent className="divide-y divide-border/60">
          {/* Verkaufspreis - prominent ganz oben, immer bearbeitbar */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 py-2" data-testid="data-field-price">
            <div className="w-full sm:w-40 sm:shrink-0 text-xs text-muted-foreground">Verkaufspreis</div>
            <div className="flex-1 min-w-0 text-sm font-bold tabular-nums truncate">
              {vehicle.price != null ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(vehicle.price) : <span className="text-amber-300 font-medium">Noch kein Preis</span>}
            </div>
            <StatusPill label={vehicle.price != null ? "Festgelegt" : "Fehlt"} color={vehicle.price != null ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"} />
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onEditPrice} data-testid="data-edit-price">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
          {EDITABLE.map((k) => {
            const m = meta[k] || {};
            const trust = m.trust || (s[k] == null ? "fehlt" : "manuell");
            return (
              <div key={k} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 py-2" data-testid={`data-field-${k}`}>
                <div className="w-full sm:w-40 sm:shrink-0 text-xs text-muted-foreground">{FIELD_LABELS[k] || k}</div>
                <div className="flex-1 min-w-0 text-sm font-medium truncate">{s[k] != null && s[k] !== "" ? String(s[k]) : <span className="text-muted-foreground">–</span>}</div>
                <StatusPill label={TRUST_META[trust]?.label || trust} color={TRUST_META[trust]?.color} hint={m.source ? `Quelle: ${SOURCE_LABELS[m.source] || m.source}` : undefined} />
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { setEdit(k); setValue(s[k] ?? ""); }} data-testid={`data-edit-${k}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="lg:col-span-5 space-y-4">
        <Card data-testid="datacheck-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base font-display">Datenprüfung</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {datacheck.missing_required.length > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <div className="text-xs font-medium text-red-300 mb-1">Fehlende Pflichtfelder</div>
                <div className="text-xs text-red-200/80">{datacheck.missing_required.map((f) => FIELD_LABELS[f] || f).join(", ")}</div>
              </div>
            )}
            {openConflicts.length === 0 && datacheck.missing_required.length === 0 && (
              <p className="text-sm text-emerald-300">Keine Konflikte, keine fehlenden Pflichtfelder.</p>
            )}
            {openConflicts.map((c) => (
              <div key={c.id} className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2" data-testid={`conflict-${c.field}`}>
                <div className="flex items-center gap-2 text-sm font-medium text-amber-300">
                  <AlertTriangle className="h-4 w-4" /> Widersprüchliche Angaben: {FIELD_LABELS[c.field] || c.field}. Bitte prüfen.
                </div>
                <div className="space-y-1.5">
                  {c.values.map((v, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded-md bg-background/40 border border-border px-2.5 py-1.5">
                      <div className="text-sm">{String(v.value)} <span className="text-xs text-muted-foreground">· {SOURCE_LABELS[v.source] || v.source}</span></div>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resolveConflict(c, v.value)} data-testid={`conflict-resolve-${c.field}-${i}`}>
                        Übernehmen
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="claims-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display">Rechtlich sensible Angaben</CardTitle>
            <p className="text-xs text-muted-foreground">Unbestätigte Angaben werden NICHT in generierten Texten oder Inseraten verwendet.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {claims.length === 0 && <p className="text-sm text-muted-foreground">Keine Angaben erfasst.</p>}
            {claims.map((c) => (
              <div key={c.id} className={`rounded-lg border p-3 space-y-1.5 ${c.confirmation_status === "confirmed" ? "border-emerald-500/30 bg-emerald-500/5" : c.confirmation_status === "rejected" ? "border-border bg-secondary/30 opacity-60" : "border-orange-500/30 bg-orange-500/10"}`} data-testid={`claim-${c.claim_type}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-muted-foreground">{CLAIM_TYPE_LABELS[c.claim_type] || c.claim_type} · Quelle: {SOURCE_LABELS[c.source] || c.source}</div>
                  <StatusPill
                    label={c.confirmation_status === "confirmed" ? `Bestätigt von ${c.confirmed_by}` : c.confirmation_status === "rejected" ? "Abgelehnt" : "Bestätigung erforderlich"}
                    color={c.confirmation_status === "confirmed" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : c.confirmation_status === "rejected" ? "bg-slate-500/20 text-slate-300 border-slate-500/30" : "bg-orange-500/15 text-orange-300 border-orange-500/30"}
                  />
                </div>
                <div className="text-sm font-medium">{c.claim_value}</div>
                {c.confirmation_status === "unconfirmed" && c.requires_confirmation && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="h-8 gap-1.5" onClick={() => claimAction(c, "confirm")} data-testid={`claim-confirm-${c.claim_type}`}>
                      <ShieldCheck className="h-3.5 w-3.5" /> Ausdrücklich bestätigen
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => claimAction(c, "reject")} data-testid={`claim-reject-${c.claim_type}`}>
                      <ShieldX className="h-3.5 w-3.5" /> Ablehnen
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{FIELD_LABELS[edit] || edit} bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Neuer Wert</Label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} type={NUMERIC.includes(edit) ? "number" : "text"} data-testid="data-edit-input" />
            <p className="text-xs text-muted-foreground">Weicht der Wert von einer bestätigten Provider-Angabe ab, wird ein Datenkonflikt erzeugt – keine stille Überschreibung.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveField} disabled={busy} data-testid="data-edit-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
